import {
	Alert,
	Box,
	Button,
	Col,
	Component,
	Grid,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	Stack,
	Text,
	TextArea,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { SelectField } from "@sembark-travel/ui-form"
import pluralize from "pluralize"
import { useMemo } from "react"
import { isArray } from "remeda"
import { SelectTouristsAndGroups } from "./List"
import { ITouristOrGroupOption } from "./NewItem"
import type { TTourist } from "./store"

export type TBookingTouristUpdate = {
	remarks: string | null
	id: number
}

export function TouristAssignmentInputField({
	name,
	tripId,
	requiredAdults,
	requiredChildren = [],
	allTourists,
	onChange,
	label = "Assign Tourists/Groups",
	primaryContact,
	onPrimaryContactChange,
	bookingTourist = [],
	onBookingTouristChange,
	showMisMatchWarning = false,
}: Readonly<{
	name: string
	tripId: number
	requiredAdults?: number
	requiredChildren?: Array<{ age: number }> | number
	allTourists?: TTourist[]
	onChange: (value: ITouristOrGroupOption[]) => void
	label?: string
	primaryContact?: number | null
	onPrimaryContactChange?: (touristId: number | null) => void
	bookingTourist?: TBookingTouristUpdate[] | undefined
	onBookingTouristChange?: (value: TBookingTouristUpdate[]) => void
	showMisMatchWarning?: boolean
}>) {
	const { xs, sm, md, lg, xl } = useBreakpoints()
	const summary = useMemo(() => {
		if (!(allTourists || []).length) return { adults: 0, children: 0 }
		const adults = (allTourists || []).filter((t) => t.type === "adult").length
		const children = (allTourists || []).filter(
			(t) => t.type === "child"
		).length

		return { adults, children }
	}, [allTourists])

	let mismatch: { type: "error" | "warning"; message: string } | null = null
	if (requiredAdults && summary?.adults > requiredAdults) {
		mismatch = {
			type: "error",
			message: `Please select only ${requiredAdults} ${pluralize("adult", requiredAdults)} for this booking. You have selected ${summary?.adults} ${pluralize("adult", summary?.adults)}.`,
		}
	}
	if (
		requiredChildren &&
		summary.children >
			(isArray(requiredChildren) ? requiredChildren.length : requiredChildren)
	) {
		mismatch = {
			type: "error",
			message: `Booking requires ${isArray(requiredChildren) ? requiredChildren.length : requiredChildren} ${pluralize("child", isArray(requiredChildren) ? requiredChildren.length : requiredChildren)}, but you have selected ${summary.children} ${pluralize("child", summary.children)}.`,
		}
	}

	return (
		<Stack gap="2">
			<Inline gap="2" justifyContent="between">
				{label ? (
					<Heading fontSize="md" as="h4">
						{label}
					</Heading>
				) : null}

				<Box fontSize={"sm"} color={"muted"} display={"flex"}>
					{joinAttributes(
						[summary.adults, <Text>{summary.adults} Adults</Text>],
						[summary.children, <Text>{summary.children} Children</Text>]
					)}
				</Box>
			</Inline>

			<Grid paddingX={"0"}>
				<Col
					xs={12}
					md={12}
					lg={6}
					paddingX={"0"}
					paddingRight={lg || xl ? "4" : "0"}
				>
					<SelectField
						name={name}
						select={SelectTouristsAndGroups}
						tripId={tripId}
						placeholder={label}
						multiple
						hideSelected
						onChange={onChange}
						width="full"
						style={{ width: "100%" }}
					/>
				</Col>

				<Col
					xs={12}
					md={12}
					lg={6}
					paddingRight={"0"}
					marginTop={xs || sm || md ? "6" : "0"}
					paddingLeft={lg || xl ? "4" : "0"}
				>
					<Stack gap="1">
						{showMisMatchWarning && mismatch && mismatch.type ? (
							<Box>
								<Alert
									inline
									status={mismatch.type as "error" | "warning" | undefined}
								>
									<Box fontSize={"sm"} paddingY={"1"}>
										{mismatch.message}
									</Box>
								</Alert>
							</Box>
						) : null}
						<Box overflowY="auto" style={{ maxHeight: "250px" }}>
							<Stack gap="2">
								{(allTourists || []).map((tourist) => {
									return (
										<Box
											key={tourist.id}
											borderWidth="1"
											borderColor="muted"
											rounded="lg"
											padding="2"
											backgroundColor="default"
											boxShadow="sm"
										>
											<Component initialState={false}>
												{({
													setState: setOpenRemarkField,
													state: isRemarkFieldOpen,
												}) => {
													const remarks =
														bookingTourist?.find((bt) => bt.id === tourist.id)
															?.remarks ||
														tourist?.booking_tourist?.remarks ||
														""
													return (
														<>
															<Stack gap="2">
																<Inline
																	justifyContent="between"
																	alignItems="center"
																>
																	<Box>
																		<Inline gap="2" alignItems="center">
																			<Box
																				rounded="full"
																				width="5"
																				height="5"
																				display="flex"
																				alignItems="center"
																				justifyContent="center"
																			>
																				{tourist.type === "child" ? (
																					<Icons.Child size={"6"} />
																				) : (
																					<Icons.User size={"6"} />
																				)}
																			</Box>
																			<Stack>
																				<Box>
																					<Box
																						as="span"
																						fontSize="sm"
																						color="muted"
																					>
																						{tourist.name.slice(0, 22)}
																						{tourist.name.length > 10
																							? "..."
																							: ""}
																					</Box>

																					{tourist.documents?.length ? (
																						<Box
																							marginLeft={"1"}
																							as="span"
																							fontSize="sm"
																							color="muted"
																						>
																							<Icons.DocumentText />
																						</Box>
																					) : null}
																				</Box>

																				<Inline alignItems="center">
																					{joinAttributes(
																						tourist.type === "child" &&
																							tourist.age_during_trip ? (
																							<Text color="muted" fontSize="sm">
																								{tourist.age_during_trip}y
																							</Text>
																						) : null,
																						tourist.phone_number ? (
																							<Text color="muted" fontSize="sm">
																								{tourist.phone_number}
																							</Text>
																						) : null
																					)}
																				</Inline>
																			</Stack>
																		</Inline>
																	</Box>

																	<Inline gap="1">
																		<Button
																			size="sm"
																			title={
																				isRemarkFieldOpen
																					? "Close Booking Remarks"
																					: "Open Booking Remarks"
																			}
																			onClick={() => {
																				setOpenRemarkField(!isRemarkFieldOpen)
																			}}
																			style={{ minWidth: 36 }}
																		>
																			<Icons.Annotation color="primary" />
																		</Button>
																		{onPrimaryContactChange &&
																		tourist.type === "adult" ? (
																			<Button
																				size="sm"
																				title={
																					tourist.id === primaryContact
																						? "Primary Contact"
																						: "Click to Make Primary Contact"
																				}
																				onClick={() => {
																					onPrimaryContactChange(
																						primaryContact === tourist.id
																							? null
																							: tourist.id
																					)
																				}}
																				style={{ minWidth: 36 }}
																			>
																				{tourist.id === primaryContact ? (
																					<Icons.FlagSolid color="primary" />
																				) : (
																					<Icons.Flag />
																				)}
																			</Button>
																		) : null}
																	</Inline>
																</Inline>
																{!isRemarkFieldOpen && (
																	<Text fontSize="sm">{remarks}</Text>
																)}
															</Stack>
															{isRemarkFieldOpen ? (
																<Box marginTop="2">
																	<Stack gap="1">
																		<Text fontSize="sm" fontWeight="semibold">
																			Booking Remarks
																		</Text>
																		<TextArea
																			value={remarks}
																			onChange={(e) => {
																				const value = e.currentTarget.value
																				const currentBookingTourists =
																					bookingTourist || []
																				const existingIndex =
																					currentBookingTourists.findIndex(
																						(bt) => bt.id === tourist.id
																					)

																				let updatedBookingTourists: TBookingTouristUpdate[]
																				if (existingIndex >= 0) {
																					// Update existing remarks
																					updatedBookingTourists = [
																						...currentBookingTourists,
																					]
																					updatedBookingTourists[
																						existingIndex
																					] = {
																						...updatedBookingTourists[
																							existingIndex
																						],
																						remarks: value || null,
																					}
																				} else {
																					// Add new entry
																					updatedBookingTourists = [
																						...currentBookingTourists,
																						{
																							id: tourist.id,
																							remarks: value || null,
																						},
																					]
																				}

																				onBookingTouristChange?.(
																					updatedBookingTourists
																				)
																			}}
																			placeholder="Additional notes about the tourist"
																			maxLength={500}
																			rows={1}
																		/>
																	</Stack>
																</Box>
															) : null}
														</>
													)
												}}
											</Component>
										</Box>
									)
								})}
							</Stack>
						</Box>
					</Stack>
				</Col>
			</Grid>
		</Stack>
	)
}
